import { getCartErrorMessage } from '@theme/cart-feedback';

/**
 * <s-matching-pair> adds two sets in one click: the variant chosen in the main
 * product form (for the shopper) and the variant chosen in this element's select
 * (for their partner or friend). The automatic "Cozy together" discount then
 * takes 15% off both. Without Standard Actions it stays hidden and the normal
 * form still works.
 */
class SMatchingPair extends HTMLElement {
  #ac;

  connectedCallback() {
    if (!window.Shopify?.actions?.updateCart) return;
    this.hidden = false;

    this.#ac = new AbortController();
    const button = this.querySelector('[data-matching-pair-add]');
    button?.addEventListener('click', () => this.#add(button), { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #mainVariantId() {
    const form = this.ownerDocument.getElementById(this.dataset.formHost || '');
    const input = form?.querySelector('input[name="id"]');
    return input instanceof HTMLInputElement ? input.value.trim() : '';
  }

  async #add(button) {
    if (this.getAttribute('aria-busy') === 'true') return;

    const select = this.querySelector('[data-matching-pair-variant]');
    const mine = this.#mainVariantId();
    const theirs = select instanceof HTMLSelectElement ? select.value.trim() : '';
    if (!mine || !theirs) {
      this.#showError(this.dataset.chooseMessage || '');
      return;
    }

    const lines = mine === theirs
      ? [{ merchandiseId: mine, quantity: 2 }]
      : [{ merchandiseId: mine, quantity: 1 }, { merchandiseId: theirs, quantity: 1 }];

    this.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-disabled', 'true');
    this.#showError('');

    try {
      const result = await window.Shopify.actions.updateCart(
        { lines },
        { event: { context: 'product', detail: { source: 's-matching-pair', productId: this.dataset.productId } } }
      );
      const message = result?.userErrors?.[0]?.message || '';
      if (message) {
        this.#showError(message);
      } else {
        await window.Shopify.actions.openCart?.();
      }
    } catch (error) {
      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      this.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
    }
  }

  #showError(message) {
    const error = this.querySelector('[data-matching-pair-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }
}

if (!customElements.get('s-matching-pair')) customElements.define('s-matching-pair', SMatchingPair);
